import React, { useState, useEffect } from 'react';
import { getBotById } from '../../services/botService';
import { toast } from 'react-hot-toast';
import styles from './BotMetrics.module.css';
import logger from '../../utils/logger';
import { ArrowUpRight, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import LeadDetails from '../leads/leadDetails/leadDetails';
import DrawerComponent from '../../components/drawer';

interface BotMetricsProps {
  botId: string;
}

interface BotData {
  _id: string;
  name: string;
  status: string;
  lastTriggerAt?: string;
  metrics?: {
    numberOfRuns: number;
    numberOfLeads: number;
  };
  totalSessions?: number;
  activeSessionsCount?: number;
  sessions?: Array<{
    _id: string;
    sessionId: string;
    whatsappNumber: string;
    chatbot: string;
    currentGroup: string;
    currentStep: number;
    responses: any;
    chatHistories: any[];
    isBotEnded: boolean;
    lastMessageTime: string;
    error?: {
      message: string;
      code: number;
      timestamp: string;
    } | null;
    lead: string | {
      _id: string;
      name?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    };
  }>;
}

const BotMetrics: React.FC<BotMetricsProps> = ({ botId }) => {
  logger.log('📊 BotMetrics: Rendering ENHANCED VERSION with session history for botId:', botId);

  const [botData, setBotData] = useState<BotData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastFetchedBotId, setLastFetchedBotId] = useState<string | null>(null);
  
  // Lead details drawer state
  const [openWithHeader, setOpenWithHeader] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [from, setFrom] = useState<string>('bot-metrics');
  
  // Chat history expansion state
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());

  // Handle lead click to open details drawer
  const onLeadClick = (lead: any) => {
    if (lead?._id) {
      setSelectedLead(lead);
      setOpenWithHeader(true);
    } else {
      toast.error('Invalid lead selected');
    }
  };

  // Toggle chat history expansion
  const toggleChatHistory = (sessionId: string) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  // Format user response based on type
  const formatUserResponse = (response: any) => {
    if (typeof response === 'string') {
      return response;
    }
    
    if (response?.body) {
      return response.body;
    }
    
    if (response?.type === 'button_reply' && response?.button_reply?.title) {
      return `🔘 ${response.button_reply.title}`;
    }
    
    if (response?.type === 'list_reply' && response?.list_reply?.title) {
      return `📋 ${response.list_reply.title}`;
    }
    
    if (response?.type === 'interactive' && response?.interactive?.button_reply?.title) {
      return `🔘 ${response.interactive.button_reply.title}`;
    }
    
    return JSON.stringify(response);
  };

  // Render chat history for a session
  const renderChatHistory = (session: any) => {
    if (!session.chatHistories || session.chatHistories.length === 0) {
      return (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#6b7280',
          fontStyle: 'italic'
        }}>
          No conversation history available
        </div>
      );
    }

    return (
      <div style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        maxHeight: '400px',
        overflowY: 'auto'
      }}>
        {session.chatHistories.map((chat: any, index: number) => (
          <div key={index} style={{
            padding: '16px',
            borderBottom: index < session.chatHistories.length - 1 ? '1px solid #f3f4f6' : 'none'
          }}>
            {/* Bot Question */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '12px'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #3FAEFD 0%, #2563eb 100%)',
                borderRadius: '20px',
                padding: '12px 16px',
                marginRight: '12px',
                maxWidth: '80%',
                color: 'white'
              }}>
                <div style={{
                  fontSize: '11px',
                  opacity: 0.8,
                  marginBottom: '4px',
                  fontWeight: '600'
                }}>
                  🤖 Bot
                </div>
                <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                  {chat.question}
                </div>
              </div>
            </div>

            {/* User Response */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                borderRadius: '20px',
                padding: '12px 16px',
                marginLeft: '12px',
                maxWidth: '80%',
                color: 'white'
              }}>
                <div style={{
                  fontSize: '11px',
                  opacity: 0.8,
                  marginBottom: '4px',
                  fontWeight: '600',
                  textAlign: 'right'
                }}>
                  👤 User
                </div>
                <div style={{ fontSize: '14px', lineHeight: '1.4', textAlign: 'right' }}>
                  {formatUserResponse(chat.response)}
                </div>
              </div>
            </div>

            {/* Timestamp */}
            {chat.timestamp && (
              <div style={{
                textAlign: 'center',
                fontSize: '11px',
                color: '#9ca3af',
                marginTop: '8px',
                fontWeight: '500'
              }}>
                {new Date(chat.timestamp).toLocaleString()}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // 🔥 MAIN FIX: Only fetch when botId actually changes
  useEffect(() => {
    const fetchBotData = async () => {
      if (!botId || botId === lastFetchedBotId) {
        logger.log('🚫 BotMetrics: Skipping fetch - same botId or empty:', { botId, lastFetchedBotId });
        if (botId && !loading) {
          return;
        }
        return;
      }

      logger.log('🔄 BotMetrics: Fetching bot data for NEW ID:', botId);
      setLoading(true);

      try {
        const response = await getBotById(botId);
        const fetchedBotData = response.data;
        
        if (!fetchedBotData) {
          throw new Error('Bot not found');
        }
        
        logger.log('✅ BotMetrics: Bot data fetched successfully:', {
          id: fetchedBotData._id,
          name: fetchedBotData.name,
          status: fetchedBotData.status,
          sessionsCount: fetchedBotData.sessions?.length || 0
        });
        
        // 🔥 NEW: Console log session data with lead info
        if (fetchedBotData.sessions) {
          logger.log('📊 Sessions with lead data:', fetchedBotData.sessions.map((s: { sessionId: any; lead: { name: any; }; }) => ({
            sessionId: s.sessionId,
            leadName: (s.lead && typeof s.lead === 'object') ? s.lead.name : 'No lead data'
          })));
        }
        
        setBotData(fetchedBotData);
        setLastFetchedBotId(botId);

      } catch (error) {
        console.error('❌ BotMetrics: Error fetching bot data:', error);
        toast.error('Failed to load bot metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchBotData();
  }, [botId]);

  logger.log('🎯 BotMetrics RENDER:', { botId, lastFetchedBotId, loading, hasBotData: !!botData });

  // Loading state
  if (loading) {
    return (
      <div className={styles.metricsContainer}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '400px',
          color: '#6b7280',
          fontFamily: 'Gilroy, sans-serif'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #3FAEFD',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p>Loading bot metrics...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (!botData) {
    return (
      <div className={styles.metricsContainer}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '400px',
          color: '#ef4444',
          fontFamily: 'Gilroy, sans-serif'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3>Failed to Load Metrics</h3>
          <p>Could not fetch bot data. Please try again.</p>
        </div>
      </div>
    );
  }

  // Calculate advanced metrics
  const totalRuns = botData.metrics?.numberOfRuns || 0;
  const totalSessions = botData.totalSessions || 0;
  const activeSessions = botData.activeSessionsCount || 0;
  const erroredSessions = botData.sessions?.filter(s => s.error)?.length || 0;
  const completedSessions = botData.sessions?.filter(s => s.isBotEnded && !s.error)?.length || 0;
  const totalLeads = botData.metrics?.numberOfLeads || 0;
  
  // Advanced calculations
  const successRate = totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0;
  const errorRate = totalSessions > 0 ? Math.round((erroredSessions / totalSessions) * 100) : 0;
  const conversionRate = totalRuns > 0 ? Math.round((totalLeads / totalRuns) * 100) : 0;
  const activeRate = totalSessions > 0 ? Math.round((activeSessions / totalSessions) * 100) : 0;

  // Format date with enhanced styling
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never triggered';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.round(diffInHours)}h ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // 🔥 NEW: Format session date specifically
  const formatSessionDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // 🔥 ENHANCED: Format WhatsApp number for display - supports international numbers
  const formatPhoneNumber = (number: string) => {
    // Clean the number
    const cleaned = number.replace(/[^\d+]/g, '');
    
    // Handle different country codes
    if (cleaned.startsWith('+91') && cleaned.length === 13) {
      // India: +91 12345 67890
      const phoneNumber = cleaned.slice(3);
      return `+91 ${phoneNumber.slice(0, 5)} ${phoneNumber.slice(5)}`;
    } else if (cleaned.startsWith('+1') && cleaned.length === 12) {
      // US/Canada: +1 (123) 456-7890
      const phoneNumber = cleaned.slice(2);
      return `+1 (${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    } else if (cleaned.startsWith('+44') && cleaned.length >= 12) {
      // UK: +44 20 1234 5678
      const phoneNumber = cleaned.slice(3);
      return `+44 ${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2, 6)} ${phoneNumber.slice(6)}`;
    } else if (cleaned.startsWith('+')) {
      // Other international: +XX XXXXX XXXXX
      const countryCode = cleaned.match(/^\+\d{1,3}/)?.[0] || '';
      const phoneNumber = cleaned.slice(countryCode.length);
      if (phoneNumber.length >= 8) {
        const mid = Math.ceil(phoneNumber.length / 2);
        return `${countryCode} ${phoneNumber.slice(0, mid)} ${phoneNumber.slice(mid)}`;
      }
      return `${countryCode} ${phoneNumber}`;
    } else if (cleaned.length === 10) {
      // Default to India format for 10-digit numbers
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    
    // Return as-is if no formatting rule matches
    return number;
  };

  // 🔥 NEW: Get lead name from session data
  const getLeadName = (session: any) => {
    logger.log('👤 Getting lead name for session:', session.sessionId, 'Lead data:', session.lead);
    
    if (typeof session.lead === 'object' && session.lead?.name) {
      return session.lead.name;
    }
    
    // Fallback: try firstName + lastName
    if (typeof session.lead === 'object' && (session.lead?.firstName || session.lead?.lastName)) {
      const firstName = session.lead.firstName || '';
      const lastName = session.lead.lastName || '';
      return `${firstName} ${lastName}`.trim();
    }
    
    return 'Unknown Lead';
  };

  // Get recent errors with enhanced details
  const recentErrors = botData.sessions
    ?.filter(s => s.error)
    ?.slice(0, 3)
    ?.map(s => ({
      message: s.error!.message,
      time: formatDate(s.error!.timestamp),
      code: s.error!.code,
      severity: s.error!.code >= 500 ? 'high' : s.error!.code >= 400 ? 'medium' : 'low'
    })) || [];

  // Determine overall health status
  const getHealthStatus = () => {
    if (errorRate === 0 && successRate > 80) return { status: 'excellent', color: '#10b981', icon: '🟢' };
    if (errorRate < 20 && successRate > 60) return { status: 'good', color: '#3FAEFD', icon: '🔵' };
    if (errorRate < 50) return { status: 'warning', color: '#f59e0b', icon: '🟡' };
    return { status: 'critical', color: '#ef4444', icon: '🔴' };
  };

  const healthStatus = getHealthStatus();

  return (
    <div className={styles.metricsContainer}>
      <div className={styles.metricsHeader}>
        <div>
          <h3>Bot Performance Analytics</h3>
         
        </div>
        
        <span className={styles.lastTrigger}>
          ⚡ {formatDate(botData.lastTriggerAt)}
        </span>
      </div>

      {/* Enhanced Metrics Cards */}
      <div className={styles.metricsGrid}>
        
        {/* Total Executions */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{totalRuns}</div>
          <div className={styles.metricLabel}>Total Executions</div>
          <div className={styles.metricIcon}>🚀</div>
        </div>

        {/* Success Rate with Dynamic Color */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{successRate}%</div>
          <div className={styles.metricLabel}>Success Rate</div>
          <div className={styles.metricIcon}>✨</div>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ 
                width: `${successRate}%`, 
                background: `linear-gradient(90deg, ${
                  successRate > 80 ? '#10b981' : 
                  successRate > 60 ? '#3FAEFD' : 
                  successRate > 40 ? '#f59e0b' : '#ef4444'
                } 0%, ${
                  successRate > 80 ? '#34d399' : 
                  successRate > 60 ? '#60a5fa' : 
                  successRate > 40 ? '#fbbf24' : '#f87171'
                } 100%)`
              }}
            ></div>
          </div>
        </div>

        {/* Chat Sessions */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{totalSessions}</div>
          <div className={styles.metricLabel}>Chat Sessions</div>
          <div className={styles.metricIcon}>💬</div>
        </div>

        {/* Active Sessions with Pulse */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{activeSessions}</div>
          <div className={styles.metricLabel}>Live Sessions</div>
          <div className={styles.metricIcon}>🔄</div>
          {activeSessions > 0 && (
            <div className={styles.statusBadge}>
              <span className={styles.activeIndicator}></span> 
              {activeSessions} Active
            </div>
          )}
        </div>

        {/* Leads Generated */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{totalLeads}</div>
          <div className={styles.metricLabel}>Leads Generated</div>
          <div className={styles.metricIcon}>👥</div>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ 
                width: `${conversionRate}%`, 
                background: 'linear-gradient(90deg, #8b5cf6 0%, #a78bfa 100%)'
              }}
            ></div>
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', fontWeight: '600' }}>
            {conversionRate}% conversion rate
          </div>
        </div>

        {/* Error Rate */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{errorRate}%</div>
          <div className={styles.metricLabel}>Error Rate</div>
          <div className={styles.metricIcon}>⚠️</div>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ 
                width: `${errorRate}%`, 
                background: 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)'
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Enhanced Session Status Breakdown */}
      <div className={styles.sessionBreakdown}>
        <h4>📈 Session Analytics</h4>
        <div className={styles.statusBars}>
          
          {/* Completed Sessions */}
          <div className={styles.statusBar}>
            <div className={styles.statusLabel}>
              <span className={styles.statusDot} style={{ backgroundColor: '#10b981' }}></span>
              Completed Sessions ({completedSessions})
            </div>
            <div className={styles.statusProgress}>
              <div 
                className={styles.statusFill} 
                style={{ 
                  width: totalSessions > 0 ? `${(completedSessions / totalSessions) * 100}%` : '0%',
                  background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
                }}
              ></div>
            </div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#10b981', minWidth: '60px' }}>
              {totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0}%
            </div>
          </div>

          {/* Active Sessions */}
          <div className={styles.statusBar}>
            <div className={styles.statusLabel}>
              <span className={styles.statusDot} style={{ backgroundColor: '#3FAEFD' }}></span>
              Active Sessions ({activeSessions})
            </div>
            <div className={styles.statusProgress}>
              <div 
                className={styles.statusFill} 
                style={{ 
                  width: totalSessions > 0 ? `${(activeSessions / totalSessions) * 100}%` : '0%',
                  background: 'linear-gradient(90deg, #3FAEFD 0%, #60a5fa 100%)'
                }}
              ></div>
            </div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#3FAEFD', minWidth: '60px' }}>
              {activeRate}%
            </div>
          </div>

          {/* Errored Sessions */}
          <div className={styles.statusBar}>
            <div className={styles.statusLabel}>
              <span className={styles.statusDot} style={{ backgroundColor: '#ef4444' }}></span>
              Failed Sessions ({erroredSessions})
            </div>
            <div className={styles.statusProgress}>
              <div 
                className={styles.statusFill} 
                style={{ 
                  width: totalSessions > 0 ? `${(erroredSessions / totalSessions) * 100}%` : '0%',
                  background: 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)'
                }}
              ></div>
            </div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#ef4444', minWidth: '60px' }}>
              {errorRate}%
            </div>
          </div>
        </div>
      </div>

      {/* 🔥 SIMPLE: Session History with Lead Names + Scrollable */}
      {botData.sessions && botData.sessions.length > 0 && (
        <div className={styles.sessionBreakdown}>
          <h4>💬 Session History ({botData.sessions.length} Sessions)</h4>
          
          {/* 🔥 SCROLLABLE: Max height container */}
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px',
            background: 'white'
          }}>
            {botData.sessions.map((session, index) => (
              <div key={session._id} style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '12px'
              }}>
                {/* Session Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <h5 style={{ margin: 0, color: '#1f2937' }}>
                    💬 Session #{index + 1}
                  </h5>
                  
                  {session.isBotEnded ? (
                    <span style={{
                      background: '#10b981',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      ✅ Completed
                    </span>
                  ) : (
                    <span style={{
                      background: '#3FAEFD',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      🔄 Active
                    </span>
                  )}
                </div>
                
                {/* Session Details - Simple Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px'
                }}>
                  
                  {/* 🔥 LEAD NAME - First and prominent */}
                  <div style={{
                    background: 'rgba(63, 174, 253, 0.1)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(63, 174, 253, 0.2)'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>
                      👤 Lead Name
                    </div>
                    <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '700' }}>
                      {getLeadName(session)}
                    </div>
                  </div>
                  
                  {/* WhatsApp Number */}
                  <div style={{
                    background: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>
                      📱 Phone
                    </div>
                    <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '600' }}>
                      {formatPhoneNumber(session.whatsappNumber)}
                    </div>
                  </div>
                  
                  {/* Last Message Time */}
                  <div style={{
                    background: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>
                      ⏰ Last Message
                    </div>
                    <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '600' }}>
                      {formatSessionDate(session.lastMessageTime)}
                    </div>
                  </div>

                  {/* Open Lead Details Button */}
                  {typeof session.lead === 'object' && session.lead?._id && (
                    <div style={{
                      background: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          onLeadClick(session.lead);
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #3FAEFD 0%, #2563eb 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(63, 174, 253, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        title="View Lead Details"
                      >
                        <ArrowUpRight size={14} />
                        View Lead
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Error Display */}
                {session.error && (
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    borderRadius: '6px',
                    padding: '12px',
                    marginTop: '12px'
                  }}>
                    <div style={{ color: '#ef4444', fontSize: '14px', fontWeight: '600' }}>
                      ❌ Error {session.error.code}: {session.error.message}
                    </div>
                  </div>
                )}

                {/* Chat History Toggle Button */}
                <div style={{
                  marginTop: '16px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <button
                    onClick={() => toggleChatHistory(session.sessionId)}
                    style={{
                      background: expandedSessions.has(session.sessionId) 
                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                        : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '25px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <MessageCircle size={16} />
                    {expandedSessions.has(session.sessionId) ? (
                      <>
                        <span>Hide Conversation</span>
                        <ChevronUp size={16} />
                      </>
                    ) : (
                      <>
                        <span>Show Conversation ({session.chatHistories?.length || 0} messages)</span>
                        <ChevronDown size={16} />
                      </>
                    )}
                  </button>
                </div>

                {/* Chat History Display */}
                {expandedSessions.has(session.sessionId) && (
                  <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '16px',
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1f2937'
                    }}>
                      <MessageCircle size={20} color="#3FAEFD" />
                      Conversation History
                    </div>
                    {renderChatHistory(session)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enhanced Recent Errors */}
      {recentErrors.length > 0 && (
        <div className={styles.errorsSection}>
          <h4>🚨 Recent Issues</h4>
          <div className={styles.errorsList}>
            {recentErrors.map((error, index) => (
              <div key={index} className={styles.errorItem}>
                <div className={styles.errorMessage}>
                  <span 
                    className={styles.errorCode}
                    style={{
                      background: error.severity === 'high' ? '#ef4444' :
                                  error.severity === 'medium' ? '#f59e0b' : '#6b7280'
                    }}
                  >
                    {error.code}
                  </span>
                  {error.message}
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6b7280', 
                    marginTop: '6px',
                    fontWeight: '600'
                  }}>
                    Severity: {error.severity.toUpperCase()}
                  </div>
                </div>
                <div className={styles.errorTime}>{error.time}</div>
              </div>
            ))}
          </div>
          
          {erroredSessions > 3 && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: '16px', 
              fontSize: '14px', 
              color: '#6b7280',
              fontWeight: '600'
            }}>
              + {erroredSessions - 3} more errors in session history
            </div>
          )}
        </div>
      )}

      {/* Enhanced Bot Status */}
      <div className={styles.botStatus}>
        <div className={`${styles.statusIndicator} ${styles[`status-${botData.status}`]}`}>
          <span className={styles.statusDot}></span>
          Bot Status: {botData.status.toUpperCase()}
          <span style={{ fontSize: '12px', opacity: '0.8' }}>
            • Last updated {formatDate(botData.lastTriggerAt)}
          </span>
        </div>
      </div>

      {/* Lead Details Drawer */}
      {selectedLead && (
        <DrawerComponent
          openWithHeader={openWithHeader}
          setOpenWithHeader={setOpenWithHeader}
          customHeader={false}
          drawerTitle="Lead Details"
          size="lg"
          className="lead_details"
        >
          <LeadDetails
            _id={selectedLead._id}
            name={selectedLead.name || 'Unknown Lead'}
            integration={selectedLead.integration || 'bot'}
            customSource={selectedLead.customSource || 'Bot Session'}
            createdAt={selectedLead.createdAt || new Date()}
            status={selectedLead.status || []}
            saleValue={selectedLead.saleValue || ''}
            website={selectedLead.website || ''}
            label={selectedLead.label || []}
            // Provide default no-op functions for callback props
            deleteLeadFromDrawer={() => {}}
            copyLeadToList={() => {}}
            moveLeadToList={() => {}}
            onLabelClick={() => {}}
            onStatusClick={() => {}}
            setFrom={setFrom}
            handleEditModal={() => {}}
            handleLeadDetailsModalOpen={() => {}}
            onLeadFileUpload={() => {}}
            editLead={() => {}}
            leadFileDelete={() => {}}
          />
        </DrawerComponent>
      )}
    </div>
  );
};

export default BotMetrics;